import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer, Box, Stack, Typography, IconButton, Button, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  ShoppingBag,        // new_sale
  Tag,                // new_listing
  Edit,               // listing_revised
  Unlink,             // listing_ended
  Bell,               // fallback
} from "lucide-react";

const TYPE_META = {
  new_sale:         { Icon: ShoppingBag, color: "#059669", label: "Sold" },
  new_listing:      { Icon: Tag,         color: "#8b5cf6", label: "Listed" },
  listing_revised:  { Icon: Edit,        color: "#d97706", label: "Revised" },
  listing_ended:    { Icon: Unlink,      color: "#737373", label: "Ended" },
};

function relTime(epochSeconds) {
  if (!epochSeconds) return "";
  const diff = Math.max(0, Math.floor(Date.now() / 1000 - Number(epochSeconds)));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  const d = new Date(Number(epochSeconds) * 1000);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Where should clicking a given activity entry navigate the user? Returns
// null when there's no obvious destination so the caller can render a
// non-clickable row.
function navTargetFor(entry) {
  const meta = entry.meta || {};
  switch (entry.type) {
    case "new_sale":
      // Sales table is filterable by item id via the search box.
      return meta.ebay_item_id
        ? `/admint/sales?q=${encodeURIComponent(meta.ebay_item_id)}`
        : "/admint/sales";
    case "new_listing":
    case "listing_revised":
    case "listing_ended":
      return meta.ebay_item_id
        ? `/admint/listings?q=${encodeURIComponent(meta.ebay_item_id)}`
        : "/admint/listings";
    default:
      return null;
  }
}

function ActivityRow({ entry, unseen, onNavigate }) {
  const meta = TYPE_META[entry.type] || { Icon: Bell, color: "#9ca3af", label: entry.type };
  const Icon = meta.Icon;
  const target = navTargetFor(entry);

  const inner = (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={1.25}
      sx={{
        px: 2,
        py: 1.25,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: unseen ? "rgba(13,91,77,0.06)" : "transparent",
        cursor: target ? "pointer" : "default",
        transition: "background-color 120ms ease",
        "&:hover": target ? { bgcolor: "action.hover" } : undefined,
      }}
      onClick={target ? () => onNavigate(target) : undefined}
    >
      <Box
        sx={{
          mt: 0.25,
          width: 28,
          height: 28,
          borderRadius: 1,
          display: "grid",
          placeItems: "center",
          color: meta.color,
          bgcolor: `${meta.color}1a`,
          flexShrink: 0,
        }}
      >
        <Icon size={15} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: unseen ? 600 : 400,
            color: "text.primary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={entry.message}
        >
          {entry.message}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
          {meta.label} · {relTime(entry.ts)}
        </Typography>
      </Box>
    </Stack>
  );

  return inner;
}

export default function NotificationsDrawer({
  open,
  onClose,
  entries,
  lastSeenAt,
  onClear,
  onRefresh,
  refreshing = false,
}) {
  const navigate = useNavigate();
  const hasEntries = Array.isArray(entries) && entries.length > 0;

  const handleNavigate = (to) => {
    onClose?.();
    navigate(to);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100vw", sm: 380 } } }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Activity</Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {onRefresh && (
            <Tooltip title="Refresh">
              <Button
                size="small"
                onClick={onRefresh}
                disabled={refreshing}
                sx={{ minWidth: 0, textTransform: "none", color: "text.secondary" }}
              >
                {refreshing ? "Refreshing…" : "Refresh"}
              </Button>
            </Tooltip>
          )}
          {hasEntries && (
            <Tooltip title="Clear all activity">
              <Button
                size="small"
                onClick={onClear}
                sx={{ minWidth: 0, textTransform: "none", color: "text.secondary" }}
              >
                Clear
              </Button>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {!hasEntries ? (
          <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
            <Bell size={28} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              No notifications yet.
            </Typography>
            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
              eBay sales and listing events will land here.
            </Typography>
          </Box>
        ) : (
          entries.map((e) => (
            <ActivityRow
              key={e.id}
              entry={e}
              unseen={Number(e.ts) > Number(lastSeenAt || 0)}
              onNavigate={handleNavigate}
            />
          ))
        )}
      </Box>
    </Drawer>
  );
}
